'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import ProtectedRoute from '@/components/ProtectedRoute'
import { CourseCard } from '@/components/courses/CourseCard'
import { CourseStats } from '@/components/courses/CourseStats'
import {
  Search,
  Filter,
  Users,
  UserPlus,
  Calendar,
  Award,
  AlertTriangle,
  TrendingUp,
  BookOpen,
  CheckCircle,
  Clock,
  Plus,
  Eye,
  Send,
  BarChart3
} from 'lucide-react'
import { 
  getAvailableCourses,
  getCoursesByStatus,
  getCoursesByType,
  type Course,
  type TeamCourse
} from '@/lib/real-data-service'
import { usePageTitle } from '@/stores/page-title-store'

function TeamMemberProgress({ teamCourse }: { teamCourse: TeamCourse }) {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-semibold text-white">
          {teamCourse.title}
        </h3>
        <span className="text-sm text-brand-primary font-medium">
          {teamCourse.teamCompletionRate}% concluído
        </span>
      </div>

      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-white">Progresso da Equipe</span>
          <span className="text-sm text-brand-gray">
            {teamCourse.enrolledEmployees.filter(e => e.status === 'completed').length} / {teamCourse.enrolledEmployees.length} concluídos
          </span>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${teamCourse.teamCompletionRate}%` }}
          />
        </div>
      </div>

      <div className="space-y-2 max-h-48 overflow-y-auto">
        {teamCourse.enrolledEmployees.map((employee) => (
          <div key={employee.id} className="flex items-center justify-between p-2 bg-brand-dark-light rounded-monumenta">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${
                employee.status === 'completed' ? 'bg-success' :
                employee.status === 'in-progress' ? 'bg-warning' :
                'bg-brand-gray'
              }`} />
              <div>
                <p className="text-white text-sm font-medium">{employee.name}</p>
                <p className="text-xs text-brand-gray">
                  {employee.status === 'completed' && employee.completionDate
                    ? `Concluído em ${new Date(employee.completionDate).toLocaleDateString('pt-BR')}`
                    : employee.status === 'in-progress'
                    ? `${employee.progress}% concluído`
                    : 'Não iniciado'
                  }
                </p>
              </div>
            </div>

            {employee.status === 'in-progress' && (
              <div className="text-right">
                <div className="w-16 h-2 bg-brand-dark rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-warning transition-all duration-300" 
                    style={{ width: `${employee.progress}%` }}
                  />
                </div>
                <span className="text-xs text-brand-gray">{employee.progress}%</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex space-x-2 pt-4 border-t border-brand-gray/20 mt-4">
        <button className="btn-primary flex-1 text-sm">
          <Eye size={14} className="mr-1" />
          Ver Detalhes
        </button>
        <button className="btn-secondary text-sm">
          <Send size={14} className="mr-1" />
          Notificar
        </button>
      </div>
    </div>
  )
}

function TeamCoursesContent() {
  const { user } = useAuth()
  usePageTitle('Cursos da Equipe')
  const [selectedTab, setSelectedTab] = useState<'overview' | 'assigned' | 'available'>('overview')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [completionFilter, setCompletionFilter] = useState<string>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [allCourses, setAllCourses] = useState<any[]>([])

  if (!user) return null

  // Check if user has team management permissions
  const canManageTeam = user.role === 'diretor-executivo' || user.role === 'diretor-financeiro' || user.role === 'vice-presidente' || user.role === 'diretor-tecnico' || user.role === 'admin'

  // Load courses data on component mount
  useEffect(() => {
    const loadCoursesData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Load courses data asynchronously
        const courses = await getAvailableCourses()
        setAllCourses(courses)
      } catch (err) {
        console.error('Error loading courses data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load courses data')
      } finally {
        setIsLoading(false)
      }
    }

    if (canManageTeam) {
      loadCoursesData()
    } else {
      setIsLoading(false)
    }
  }, [canManageTeam])

  if (!canManageTeam) {
    return (
      
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            Acesso Restrito
          </h3>
          <p className="text-gray-400">
            Você não tem permissão para gerenciar cursos da equipe
          </p>
        </div>
      
    )
  }

  // Convert courses to team courses - enrollment data will come from API
  const teamCourses: TeamCourse[] = useMemo(() => {
    return allCourses.map(course => ({
      ...course,
      enrolledEmployees: [],
      teamCompletionRate: 0
    }))
  }, [allCourses])

  // Calculate team statistics
  const { totalAssignedCourses, totalEnrollments, completedEnrollments, averageCompletion } = useMemo(() => {
    const totalAssignedCourses = teamCourses.length
    const totalEnrollments = teamCourses.reduce((sum, course) => sum + course.enrolledEmployees.length, 0)
    const completedEnrollments = teamCourses.reduce((sum, course) =>
      sum + course.enrolledEmployees.filter(e => e.status === 'completed').length, 0
    )
    const averageCompletion = totalEnrollments > 0 ? (completedEnrollments / totalEnrollments) * 100 : 0

    return { totalAssignedCourses, totalEnrollments, completedEnrollments, averageCompletion }
  }, [teamCourses])

  // Filter team courses
  const filteredTeamCourses = useMemo(() => {
    return teamCourses.filter(course => {
      const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           course.provider.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'completed' && course.teamCompletionRate === 100) ||
        (statusFilter === 'in-progress' && course.teamCompletionRate > 0 && course.teamCompletionRate < 100) ||
        (statusFilter === 'not-started' && course.teamCompletionRate === 0)

      const matchesCompletion = completionFilter === 'all' ||
        (completionFilter === 'high' && course.teamCompletionRate >= 80) ||
        (completionFilter === 'medium' && course.teamCompletionRate >= 50 && course.teamCompletionRate < 80) ||
        (completionFilter === 'low' && course.teamCompletionRate < 50)

      return matchesSearch && matchesStatus && matchesCompletion
    })
  }, [teamCourses, searchTerm, statusFilter, completionFilter])

  const handleAssignCourse = (courseId: string) => {
    // In real app: open team selection modal
  }

  const handleViewTeamProgress = (courseId: string) => {
    // In real app: open detailed progress view
  }

  if (isLoading) {
    return (
      
        <div className="space-y-4 sm:space-y-6">

          {/* Loading State */}
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
            <h3 className="font-display text-lg font-medium text-white mb-2">
              Carregando cursos da equipe...
            </h3>
            <p className="text-brand-gray max-w-md mx-auto">
              Buscando informações de cursos disponíveis para a equipe
            </p>
          </div>
        </div>
      
    )
  }

  if (error) {
    return (
      
        <div className="space-y-4 sm:space-y-6">

          {/* Error State */}
          <div className="text-center py-12">
            <div className="text-brand-red mb-4">
              <AlertTriangle className="mx-auto h-12 w-12" />
            </div>
            <h3 className="font-display text-lg font-medium text-white mb-2">
              Erro ao carregar cursos
            </h3>
            <p className="text-brand-gray max-w-md mx-auto mb-4">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      
    )
  }

  return (
    
      <div className="space-y-4 sm:space-y-6">
        {/* Action Buttons */}
        {allCourses.length > 0 && (
          <div className="flex justify-end">
            <button className="btn-primary flex items-center justify-center gap-2">
              <UserPlus size={20} />
              <span>Atribuir Curso</span>
            </button>
          </div>
        )}

        {/* Primary Empty State - No courses available */}
        {!isLoading && !error && allCourses.length === 0 && (
          <div className="card">
            <div className="flex flex-col items-center justify-center text-center py-12 px-4">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-brand-primary/20 rounded-full blur-2xl animate-pulse"></div>
                <BookOpen className="h-16 w-16 text-brand-primary relative z-10" />
              </div>
              <h3 className="font-display text-2xl font-bold text-white mb-4">
                Configure cursos para sua equipe
              </h3>
              <p className="text-brand-gray leading-relaxed mb-8 max-w-2xl">
                O módulo de Cursos da Equipe permite atribuir e acompanhar o desenvolvimento profissional
                dos seus colaboradores. Cadastre cursos obrigatórios, técnicos ou comportamentais e
                monitore o progresso em tempo real.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Cadastrar Primeiro Curso
                </button>
                <button className="btn-secondary">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Ver Exemplo de Curso
                </button>
              </div>
              <div className="mt-8 text-sm text-brand-gray">
                Dica: Você pode integrar cursos de plataformas externas ou criar cursos internos
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        {allCourses.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <BookOpen className="text-brand-primary" size={20} />
            </div>
            <div className="metric-value">{totalAssignedCourses}</div>
            <div className="metric-label">Cursos Atribuídos</div>
          </div>

          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-info" size={20} />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-info mb-2">{totalEnrollments}</div>
            <div className="metric-label">Inscrições Totais</div>
          </div>

          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-success" size={20} />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-success mb-2">{completedEnrollments}</div>
            <div className="metric-label">Concluídos</div>
          </div>

          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-warning" size={20} />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-warning mb-2">{Math.round(averageCompletion)}%</div>
            <div className="metric-label">Taxa de Conclusão</div>
          </div>
        </div>
        )}

        {/* Tabs */}
        {allCourses.length > 0 && (
        <div className="border-b border-brand-gray border-opacity-20">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {[
              { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
              { id: 'assigned', label: 'Cursos Atribuídos', icon: Users },
              { id: 'available', label: 'Disponíveis', icon: BookOpen }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-display font-medium text-sm transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${
                  selectedTab === tab.id
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-brand-gray hover:text-white hover:border-brand-gray'
                }`}
              >
                <tab.icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
        )}

        {/* Tab Content - Only show if courses exist */}
        {allCourses.length > 0 && (
        <>
        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <div className="space-y-6">
            {/* Team Progress Cards */}
            <div>
              <h2 className="text-xl font-display font-semibold text-white mb-4">
                Progresso dos Cursos Atribuídos
              </h2>
              {teamCourses.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {teamCourses.map(course => (
                    <TeamMemberProgress key={course.id} teamCourse={course} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Nenhum curso atribuído
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Comece atribuindo cursos para sua equipe
                  </p>
                  <button className="btn-primary">
                    <UserPlus size={16} className="mr-2" />
                    Atribuir Primeiro Curso
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assigned Courses Tab */}
        {selectedTab === 'assigned' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10" size={20} />
                <input
                  type="text"
                  placeholder="Buscar cursos atribuídos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input w-full pl-10"
                  style={{ paddingLeft: '3rem' }}
                />
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-input w-full sm:w-auto"
                >
                  <option value="all">Todos os Status</option>
                  <option value="completed">Concluídos</option>
                  <option value="in-progress">Em Progresso</option>
                  <option value="not-started">Não Iniciados</option>
                </select>
                
                <select
                  value={completionFilter}
                  onChange={(e) => setCompletionFilter(e.target.value)}
                  className="form-input w-full sm:w-auto"
                >
                  <option value="all">Taxa de Conclusão</option>
                  <option value="high">Alta (&gt;80%)</option>
                  <option value="medium">Média (50-80%)</option>
                  <option value="low">Baixa (&lt;50%)</option>
                </select>
              </div>
            </div>

            {/* Results */}
            <div className="text-sm text-gray-400 mb-4">
              {filteredTeamCourses.length} curso{filteredTeamCourses.length !== 1 ? 's' : ''} encontrado{filteredTeamCourses.length !== 1 ? 's' : ''}
            </div>

            {/* Team Courses Grid */}
            {filteredTeamCourses.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredTeamCourses.map(course => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    showTeamData={true}
                    onViewDetails={handleViewTeamProgress}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">
                  Nenhum curso encontrado
                </h3>
                <p className="text-gray-400 mb-4">
                  Tente ajustar os filtros ou termos de busca
                </p>
                <button 
                  onClick={() => {
                    setSearchTerm('')
                    setStatusFilter('all')
                    setCompletionFilter('all')
                  }}
                  className="btn-secondary"
                >
                  Limpar Filtros
                </button>
              </div>
            )}
          </div>
        )}

        {/* Available Courses Tab */}
        {selectedTab === 'available' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-brand-gray">
                Selecione cursos para atribuir à sua equipe
              </p>
              <button className="btn-secondary text-sm">
                <Filter size={16} className="mr-1" />
                Filtros Avançados
              </button>
            </div>

            {/* Available Courses Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {allCourses.slice(0, 6).map(course => (
                <CourseCard
                  key={course.id}
                  course={course}
                  onStartCourse={handleAssignCourse}
                />
              ))}
            </div>
          </div>
        )}
        </>
        )}

      </div>
    
  )
}

export default function TeamCoursesPage() {
  return (
    <ProtectedRoute>
      <TeamCoursesContent />
    </ProtectedRoute>
  )
}
